import type { PortalTaskAttachment, PortalTaskComment, PortalTaskDetail, PortalWorkItemListItem } from '../../modules/my-tasks/model/myTasks';
import { formatShanghai } from '../../modules/my-tasks/model/myTasks';
import type { MobileAttachmentInfo, MobileTask } from '../types';

export function attachmentModel(file: PortalTaskAttachment): MobileAttachmentInfo {
  return { name: file.name, size: file.size, fileAssetPublicId: file.fileAssetPublicId,
    kind: file.mimeType.startsWith('image/') ? 'image' : 'file' };
}

/** Presentation only: request identity, action gates and sessions stay in MyTasksWorkspace. */
export function taskModel(summary: PortalWorkItemListItem, detail?: PortalTaskDetail,
  comments: readonly PortalTaskComment[] = detail?.comments ?? []): MobileTask {
  const initiation = detail?.flowPageContext === null ? detail.processInitiationViewContext : undefined;
  const start = initiation?.startSnapshot;
  return {
    id: summary.taskId, initiatedCardPublicId: summary.initiatedCardPublicId,
    processInstancePublicId: summary.processInstancePublicId ?? detail?.flowPageContext?.processInstancePublicId,
    group: summary.category, driver: 'process', title: detail ? summary.title : summary.processDefinitionName,
    status: summary.status, source: summary.sourceSystem, initiator: summary.initiator,
    startedAt: formatShanghai(detail?.traceNodes[0]?.records[0]?.handledAt ?? summary.receivedAt),
    receivedAt: formatShanghai(summary.receivedAt), updatedAt: formatShanghai(summary.updatedAt),
    summary: summary.summary, urgent: summary.urgent, unread: summary.unread,
    objectName: summary.relatedObject?.name ?? '', objectCode: summary.relatedObject?.code ?? '',
    summaryItems: summary.summaryItems?.slice(0, 4) ?? [], sourceForm: !!detail,
    sections: [{ title: '任务信息', fields: [
      { label: '公开标识', value: summary.taskId },
      { label: '流程名称', value: summary.processDefinitionName },
      ...(summary.initiatedCardPublicId ? [{ label: '发起卡标识', value: summary.initiatedCardPublicId }] : []),
      { label: '流程实例', value: summary.processInstancePublicId ?? detail?.processInstanceId ?? '-' },
      { label: '当前节点', value: summary.currentStage ?? summary.taskName ?? '-' },
      { label: '来源状态', value: summary.sourceState },
      { label: '来源版本', value: summary.sourceVersion ?? '-' },
      ...(summary.summary ? [{ label: '摘要', value: summary.summary }] : []),
      ...(summary.summaryItems ?? []),
    ] }, ...(initiation ? [{ title: '启动回执快照', fields: start ? [
      { label: '启动回执标识', value: start.receiptPublicId },
      { label: '来源表单标识', value: start.sourceFormPublicId },
      { label: '启动版本', value: start.version },
      { label: '快照 Schema', value: start.schemaVersion },
      { label: '快照 Hash', value: start.hash },
      { label: '启动快照数据', value: JSON.stringify(start.data) },
    ] : [{ label: '启动快照', value: '未提供' }] }] : [])],
    // Every participant/operation is represented, including multiple records on one node.
    trace: detail?.traceNodes.flatMap(node => node.records.length
      ? node.records.map((record, index) => ({ id: `${node.nodeId}:${record.operationId ?? record.participantId ?? index}:${index}`,
        name: node.nodeName, actor: record.actor, action: record.action, opinion: record.opinion,
        time: formatShanghai(record.handledAt), status: node.status, attachments: record.attachments.map(attachmentModel) }))
      : [{ id: node.nodeId, name: node.nodeName, actor: '-', action: node.itemState, opinion: undefined,
        time: formatShanghai(node.startedAt), status: node.status, attachments: [] }]) ?? [],
    attachments: detail?.attachments.map(attachmentModel) ?? [],
    comments: comments.map(comment => {
      const parent = comments.find(item => item.commentId === comment.parentCommentId);
      return { id: comment.commentId, author: comment.author, content: comment.content,
        time: formatShanghai(comment.createdAt), replyTo: comment.parentCommentId ? {
          id: comment.parentCommentId, author: parent?.author ?? comment.replyTo ?? '较早评论',
          content: comment.parentDeleted ? '评论已删除' : parent?.content ?? comment.parentContent ?? '原评论暂不可见',
        } : undefined };
    }), actions: [],
  };
}
